import React from 'react';
import { formatDateTimeAsStored } from '../../utils/utils';

const SORTABLE_COLUMNS = [
  { key: "loanId", label: "Loan ID" },
  { key: "loanName", label: "Loan Name" },
  { key: "parentLoanName", label: "Parent Loan Name" },
  { key: "txHash", label: "Transaction Hash" },
  { key: "createdDate", label: "Created Date" },
];

const LoanList = ({
  loanData,
  handleLoanSelection,
  currentPage = 1,
  onPageChange,
  isLoading = false,
  sortBy,
  order,
  onSortChange,
}) => {
  // If no loanData or loading, show appropriate message
  if (isLoading) {
    return (
      <div className="loan-container">
        <div className="loading-message">Loading loans...</div>
      </div>
    );
  }

  if (!loanData || !loanData.data) {
    return (
      <div className="loan-container">
        <div className="no-data-message">No loan data available</div>
      </div>
    );
  }

  const { data: currentItems, pagination } = loanData;

  // Handle page change
  const handlePageChange = (pageNumber) => {
    if (onPageChange && pageNumber !== pagination.currentPage) {
      onPageChange(pageNumber);
    }
  };

  // Generate page numbers with ellipsis
  const generatePageNumbers = () => {
    const { currentPage: currPage, totalPages } = pagination;
    const pages = [];
    
    if (totalPages <= 7) {
      // Show all pages if total pages is 7 or less
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      // Calculate start and end pages
      let startPage = Math.max(2, currPage - 1);
      let endPage = Math.min(totalPages - 1, currPage + 1);
      
      // Add ellipsis and middle pages
      if (currPage > 3) {
        pages.push('...');
      }
      
      for (let i = startPage; i <= endPage; i++) {
        if (i > 1 && i < totalPages) {
          pages.push(i);
        }
      }
      
      if (currPage < totalPages - 2) {
        pages.push('...');
      }
      
      // Always show last page
      pages.push(totalPages);
    }
    
    return pages;
  };

  return (
    <div className="loan-container">    

      {/* Loans Table */}
      <table className="loan-table">
        <thead>
          <tr>
            {SORTABLE_COLUMNS.map(({ key, label }) => {
              const active = sortBy === key;
              const indicator = active ? (order === "asc" ? " ▲" : " ▼") : "";
              const clickable = typeof onSortChange === "function";
              return (
                <th
                  key={key}
                  onClick={clickable ? () => onSortChange(key) : undefined}
                  style={clickable ? { cursor: "pointer", userSelect: "none" } : undefined}
                  aria-sort={active ? (order === "asc" ? "ascending" : "descending") : "none"}
                >
                  {label}{indicator}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {currentItems.length === 0 ? (
            <tr>
              <td colSpan="5" className="no-data">No loans found</td>
            </tr>
          ) : (
            currentItems.map(({ loanId, metadata }, index) => (
              <tr 
                key={`${loanId}-${index}`} 
                onClick={() => handleLoanSelection(loanId)}
                className="loan-row"
              >
                <td>{loanId}</td>
                <td>{metadata?.loanName || 'N/A'}</td>
                <td>{metadata?.loanParent || 'N/A'}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  {metadata?.loanTxHash ? (
                    <span
                      className="tx-hash"
                      title={metadata.loanTxHash}
                      onClick={() => window.open(`${process.env.REACT_APP_EXP_URL}${metadata.loanTxHash}`, '_blank')}
                    >
                      {metadata.loanTxHash.slice(0, 8)}…{metadata.loanTxHash.slice(-8)}
                    </span>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td>{formatDateTimeAsStored(metadata?.createdAt)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Pagination Controls */}
      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button
            className="arrow-button"
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={!pagination.hasPrev || isLoading}
          >
            &larr;
          </button>

          {generatePageNumbers().map((page, index) => (
            page === '...' ? (
              <span key={`ellipsis-${index}`} className="ellipsis">...</span>
            ) : (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                disabled={isLoading}
                className={`page-button ${pagination.currentPage === page ? "active" : ""}`}
              >
                {page}
              </button>
            )
          ))}

          <button
            className="arrow-button"
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={!pagination.hasNext || isLoading}
          >
            &rarr;
          </button>
        </div>
      )}
    </div>
  );
};

export default LoanList;